import { memo } from 'react';
import { Handle, Position, type NodeProps } from 'reactflow';
import { UserCheck } from 'lucide-react'; // Icon for Approval

// Define the data interface for the node
interface ApprovalNodeData {
    label?: string;
    assignee?: string; // "user:..." or "role:..."
    title?: string;
    description?: string;
}

export const ApprovalNode = memo(({ data, isConnectable, selected }: NodeProps<ApprovalNodeData>) => {
    return (
        <div className={`
            px-4 py-3 rounded-lg shadow-lg border-2 bg-white min-w-[180px]
            ${selected ? 'border-amber-500 shadow-amber-100 ring-2 ring-amber-200' : 'border-slate-200 hover:border-amber-300'}
            transition-all duration-200
        `}>
            {/* Input Handle */}
            <Handle
                type="target"
                position={Position.Left}
                isConnectable={isConnectable}
                className="w-3 h-3 bg-slate-400 !border-2 !border-white"
            />

            <div className="flex items-center gap-3">
                <div className={`
                    p-2 rounded-lg shrink-0
                    ${selected ? 'bg-amber-100 text-amber-600' : 'bg-amber-50 text-amber-500'}
                `}>
                    <UserCheck size={18} />
                </div>

                <div className="flex flex-col overflow-hidden">
                    <span className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                        Approval
                    </span>
                    <span className="text-xs text-slate-500 truncate font-medium" title={data.assignee || "Unassigned"}>
                        {data.assignee || "Select Assignee"}
                    </span>
                </div>
            </div>

            {/* Approved Output */}
            <Handle
                type="source"
                position={Position.Right}
                id="approved"
                isConnectable={isConnectable}
                className="w-3 h-3 bg-green-500 !border-2 !border-white"
                style={{ top: '30%' }}
            />
            {selected && (
                <div className="absolute -right-12 top-[25%] text-[9px] font-bold text-green-600 bg-green-50 px-1 py-0.5 rounded">
                    YES
                </div>
            )}

            {/* Rejected Output */}
            <Handle
                type="source"
                position={Position.Right}
                id="rejected"
                isConnectable={isConnectable}
                className="w-3 h-3 bg-red-500 !border-2 !border-white"
                style={{ top: '70%' }}
            />
            {selected && (
                <div className="absolute -right-12 top-[65%] text-[9px] font-bold text-red-600 bg-red-50 px-1 py-0.5 rounded">
                    NO
                </div>
            )}
        </div>
    );
});
